import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "src/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95 transition-all duration-200 tracking-normal font-medium",
  {
    variants: {
      variant: {
        default:
          "bg-tailwind_primary text-tailwind_primary_foreground hover:bg-tailwind_primary/80 active:scale-95 transition-all duration-200 active:bg-tailwind_primary/70",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-95 transition-all duration-200 active:bg-destructive/70",
        outline:
          "border-[1px] text-muted-foreground border-input bg-background hover:bg-foreground/5 hover:text-foreground active:bg-foreground/10",
        secondary:
          "bg-tailwind_secondary/90 text-tailwind_secondary_foreground hover:bg-tailwind_secondary/[0.85] hover:brightness-[0.95] active:bg-tailwind_secondary/[0.85] active:brightness-[0.9] transition-all duration-200",
        primary: "bg-black/80 text-white hover:bg-black/60 active:scale-95 transition-all duration-200 active:bg-black/70",
        ghost: "text-foreground hover:text-primary active:text-primary/80 rounded-none",
        link: "text-primary underline-offset-4 hover:underline p-0",
        transparent: "text-foreground hover:bg-primary/5 active:bg-primary/10",
        icon: "text-foreground hover:text-primary active:text-primary/80 rounded-none",
        duola:
          "bg-tailwind_primary/10 text-foreground hover:bg-tailwind_primary/20 active:scale-95 transition-all duration-200 active:bg-tailwind_primary/70",
      },
      size: {
        default: "h-9 text-xs relative transition-all duration-200 px-3 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8 text-base",
        icon: "h-10 w-10",
        xs: "h-9 px-3 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), loading && "opacity-50")}
        disabled={loading || props.disabled}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
